import { forwardRef, type InputHTMLAttributes, type Ref } from 'react';
import { type FieldPath, type FieldPathValue, type FieldValues } from 'react-hook-form';
import {
  CheckedIcon,
  UncheckedIcon,
  StyledInput,
  StyledContainer,
  StyledLabel,
} from './Choice.styles';

interface ChoiceProps<T extends FieldValues, P extends FieldPath<T> = FieldPath<T>>
  extends InputHTMLAttributes<HTMLInputElement> {
  id: string;
  value?: FieldPathValue<T, P> extends Array<infer U>
    ? U extends string | number
      ? U
      : never
    : FieldPathValue<T, P> extends string | number
      ? FieldPathValue<T, P>
      : never;
  label: string | number;
  disabled?: boolean;
  type: 'radio' | 'checkbox';
  productId?: string;
}

const icons: Record<'radio' | 'checkbox', { checked: IconName; unchecked: IconName }> = {
  checkbox: {
    checked: 'bxs-checkbox-checked',
    unchecked: 'bx-checkbox',
  },
  radio: {
    checked: 'bx-radio-circle-marked',
    unchecked: 'bx-radio-circle',
  },
};

const Choice = forwardRef(
  <T extends FieldValues, P extends FieldPath<T> = FieldPath<T>>(
    { id, value, label, disabled, ...rest }: ChoiceProps<T, P>,
    ref?: Ref<HTMLInputElement>
  ) => {
    return (
      <StyledContainer isDisabled={disabled} key={id}>
        <StyledInput disabled={disabled} id={id} value={value} {...rest} ref={ref} />
        <CheckedIcon name={icons[rest.type].checked} />
        <UncheckedIcon name={icons[rest.type].unchecked} />
        <StyledLabel htmlFor={id}>{label}</StyledLabel>
      </StyledContainer>
    );
  }
);

export default Choice;
